import React, { useState, useEffect } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { ResponsiveImage, Modal, Button } from '../../components';

import ImageCarousel from './ImageCarousel/ImageCarousel';
import ActionBarMaybe from './ActionBarMaybe';

import css from './ListingPage.module.css';

const SectionHero = props => {
  const {
    title,
    listing,
    isOwnListing,
    editParams,
    handleViewPhotosClick,
    imageCarouselOpen,
    onImageCarouselClose,
    onManageDisableScrolling,
    noPayoutDetailsSetWithOwnListing,
    isIframe = false,
  } = props;

  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [fetchingSignedUrls, setFetchingSignedUrls] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const hasImages = listing.images && listing.images.length > 0;
  const firstImage = hasImages ? listing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith('scaled'))
    : [];
  const videos = listing.attributes.publicData.videos || [];

  // Prepare media array: videos first, then images
  const media = [
    ...uploadedVideos.map(v => ({ type: 'video', url: v.url, filename: v.filename })),
    ...(listing.images || []).map(img => ({ type: 'image', image: img })),
  ];

  const mediaCount = media.length;

  useEffect(() => {
    if (videos.length > 0 && !fetchingSignedUrls) {
      setFetchingSignedUrls(true);
      const videosWithoutUrl = videos.filter(video => !uploadedVideos.some(v => v.filename === video));

      // Use CloudFront URL for each video that doesn't have url
      videosWithoutUrl.forEach(video => {
        if (video) {
          const cloudfrontUrl = `${process.env.REACT_APP_CLOUDFRONT_URL}/${video}`;
          setUploadedVideos(prevState => [...prevState, { filename: video, url: cloudfrontUrl }]);
        }
      });
      setFetchingSignedUrls(false);
    }
  }, [videos, uploadedVideos]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSave = () => {
    setIsSaved(!isSaved);
  };

  const viewPhotosButton = (hasImages || videos.length > 0) ? (
    <button className={css.viewPhotos} onClick={handleViewPhotosClick}>
      <FormattedMessage
        id="ListingPage.viewImagesButton"
        values={{ count: listing.images.length + videos.length }}
      />
    </button>
  ) : null;

  const renderMedia = (item, idx, isHero = false) => {
    if (!item) return <div className={css.galleryPlaceholder}>Gallery</div>;
    if (item.type === 'video') {
      return (
        <video
          className={isHero ? css.heroVideo : css.galleryImage}
          controls={isHero}
          loop
          muted
          playsInline
          autoPlay={isHero}
          poster={isHero && listing.images && listing.images[0] ? listing.images[0].attributes.variants['scaled-large']?.url : undefined}
        >
          <source src={item.url} type="video/mp4" />
        </video>
      );
    }
    return (
      <ResponsiveImage
        rootClassName={isHero ? css.rootForImage : css.galleryImage}
        alt={`Gallery image ${idx + 1}`}
        image={item.image}
        variants={variants}
      />
    );
  };

  let layout;
  if (mediaCount === 1) {
    // Full size
    layout = (
      <div className={css.heroSingle} onClick={handleViewPhotosClick}>
        {renderMedia(media[0], 0, true)}
        {viewPhotosButton}
      </div>
    );
  } else if (mediaCount === 2) {
    // 50/50 split
    layout = (
      <div className={css.heroGridTwo}>
        <div className={css.heroHalf} onClick={handleViewPhotosClick}>
          {renderMedia(media[0], 0, true)}
        </div>
        <div className={css.heroHalf} onClick={handleViewPhotosClick}>
          {renderMedia(media[1], 1, true)}
        </div>
        {viewPhotosButton}
      </div>
    );
  } else if (mediaCount === 3 || mediaCount === 4) {
    // 1 half, 2x1 grid
    layout = (
      <div className={css.heroGridThreeFour}>
        <div className={css.heroMain} onClick={handleViewPhotosClick}>
          {renderMedia(media[0], 0, true)}
        </div>
        <div className={css.galleryGrid2x1} onClick={handleViewPhotosClick}>
          {[1, 2, 3].slice(0, mediaCount - 1).map(idx => (
            <div className={css.galleryItem} key={idx}>
              {renderMedia(media[idx], idx)}
            </div>
          ))}
        </div>
        {viewPhotosButton}
      </div>
    );
  } else {
    // 1 half, 2x2 grid
    layout = (
      <div className={css.heroGrid}>
        <div className={css.heroMain} onClick={handleViewPhotosClick}>
          {renderMedia(media[0], 0, true)}
        </div>
        <div className={css.galleryGrid} onClick={handleViewPhotosClick}>
          {[1, 2, 3, 4].map(idx => (
            <div className={css.galleryItem} key={idx}>
              {renderMedia(media[idx], idx)}
            </div>
          ))}
        </div>
        {viewPhotosButton}
      </div>
    );
  }

  // Manejar click en thumbnail
  const handleThumbClick = (idx, e) => {
    setCarouselIndex(idx);
    if (typeof handleViewPhotosClick === 'function') {
      handleViewPhotosClick(e);
    }
  };

  return (
    <div className={css.sectionHero} data-testid="hero">
      <div className={css.heroTitleContainer}>
        <h1 className={css.heroTitle}>{title}</h1>
        <div className={css.heroActions}>
          <button 
            className={`${css.heroActionButton} ${css.shareButton}`}
            onClick={handleShare}
            title="Share"
            disabled={copied}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.19966C7.54305 8.50326 6.82242 8 6 8C4.34315 8 3 9.34315 3 11C3 12.6569 4.34315 14 6 14C6.82242 14 7.54305 13.4967 8.08261 12.8003L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C17.1776 14 16.457 14.5033 15.9174 15.1997L8.97733 11.3706C8.99229 11.2492 9 11.1255 9 11C9 10.8745 8.99229 10.7508 8.97733 10.6294L15.9174 6.80034C16.457 7.49674 17.1776 8 18 8Z" fill="currentColor"/>
            </svg>
          </button>
          {copied && <span className={css.copiedFeedback}>Link copied!</span>}
        </div>
      </div>
      {layout}
      {mediaCount > 1 && (
        <div className={css.mobileThumbGallery}>
          {media.slice(1).map((item, idx) => (
            <div
              className={css.mobileThumbItem}
              key={idx}
              onClick={e => handleThumbClick(idx + 1, e)}
              style={{ cursor: 'pointer' }}
            >
              {item.type === 'video' ? (
                <video className={css.mobileThumbImage} muted playsInline>
                  <source src={item.url} type="video/mp4" />
                </video>
              ) : (
                <ResponsiveImage
                  rootClassName={css.mobileThumbImage}
                  alt={`Thumbnail ${idx + 2}`}
                  image={item.image}
                  variants={variants}
                />
              )}
            </div>
          ))}
        </div>
      )}
      <Modal
        id="ListingPage.imageCarousel"
        scrollLayerClassName={css.carouselModalScrollLayer}
        containerClassName={css.carouselModalContainer}
        lightCloseButton
        isOpen={imageCarouselOpen}
        onClose={onImageCarouselClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ImageCarousel
          images={listing.images}
          videos={uploadedVideos}
          imageVariants={['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge']}
          startIndex={carouselIndex}
        />
      </Modal>
    </div>
  );
};

export default SectionHero;
